'use client'

import { useEffect, useState } from 'react'

import { useFraudStore, useImportScript } from '@rewardops/ui'
import { Logger, type WindowType } from '@rewardops/utils'

declare let window: WindowType & {
  ka: any
}

interface KountProps {
  apiUrl: string
  merchantId: string
}

/**
 * Kount Command Device Data Collector (Legacy)
 * This is the old implementation that loads the Kount Command SDK via script tag.
 * It will be deprecated in favor of Kount 360.
 */
const Kount = ({ apiUrl, merchantId }: KountProps) => {
  const [hasAttemptedLoad, setHasAttemptedLoad] = useState(false)
  const setFraudSessionId = useFraudStore((state) => state.setFraud)

  const [isLoaded, error] = useImportScript(
    `${apiUrl}/collect/sdk?m=${merchantId}`,
    { async: true },
    () => {
      if (hasAttemptedLoad) {
        return
      }

      const kaSessionId = window?.ka?.sessionId
      setFraudSessionId(kaSessionId)

      // kount never mounted to application global, continue without it
      if (!window.ka || !kaSessionId) {
        setFraudSessionId(kaSessionId)
        return
      }

      const kountClient = new window.ka.ClientSDK()

      // something is wrong, kount has no method for setting up callback
      if (!kountClient.setupCallback) {
        setFraudSessionId(kaSessionId)
        return
      }

      // lets proceed to set up callback but first we set a timeout reference
      let kountTimeout: number | NodeJS.Timeout

      kountClient.setupCallback({
        'collect-end': () => {
          clearTimeout(kountTimeout as number)
          setFraudSessionId(kaSessionId)
        },

        'collect-begin': () => {
          // Get the iframe element
          const iframe = document.getElementById('ibody')

          // i18n fix: Add aria-label to iframe
          if (iframe) {
            iframe.setAttribute('aria-label', 'Anti-fraud verification')
          }

          kountTimeout = setTimeout(() => {
            // kount never resolved, continue without it
            setFraudSessionId(kaSessionId)
          }, 5000)
        },
      })

      // initiate kount to collect data
      kountClient.autoLoadEvents()
    },
  )

  useEffect(() => {
    if (!hasAttemptedLoad && (isLoaded || error)) {
      setHasAttemptedLoad(true)
    }

    if (error) {
      Logger.error('Error loading Kount Command script:', error)
    }
  }, [isLoaded, error])

  return undefined
}

export { Kount }
